import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Icons,
  iconSize,
  Slider,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@munifocus/ui';
import type { Evidence } from '@munifocus/contracts-gov';
import { formatDateTime } from '../../lib/relative-time';

type Mode = 'slider' | 'side';

/** Two photos of the same spot: a wipe slider, or side by side. */
export function BeforeAfterView({ before, after }: { before: Evidence; after: Evidence }) {
  const { t, i18n } = useTranslation();
  const [mode, setMode] = useState<Mode>('slider');
  const [split, setSplit] = useState(50);

  const caption = (label: string, e: Evidence) => (
    <Typography variant="caption" color="text.secondary">
      {label} · {formatDateTime(e.capturedAt, i18n.language)}
    </Typography>
  );

  return (
    <Stack spacing={1}>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={mode}
        onChange={(_, v: Mode | null) => v && setMode(v)}
        aria-label={t('gov.evidenceMarkup.compareMode')}
      >
        <ToggleButton value="slider">
          <Icons.Columns2 size={iconSize.sm} />
          <Box component="span" sx={{ ml: 0.75 }}>
            {t('gov.evidenceMarkup.slider')}
          </Box>
        </ToggleButton>
        <ToggleButton value="side">
          <Icons.Images size={iconSize.sm} />
          <Box component="span" sx={{ ml: 0.75 }}>
            {t('gov.evidenceMarkup.sideBySide')}
          </Box>
        </ToggleButton>
      </ToggleButtonGroup>

      {mode === 'side' ? (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          {[
            [t('gov.evidenceMarkup.before'), before] as const,
            [t('gov.evidenceMarkup.after'), after] as const,
          ].map(([label, e]) => (
            <Stack key={e.id} spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
              <Box
                component="img"
                src={e.downloadUrl}
                alt={`${label}: ${e.originalFilename}`}
                sx={{ width: '100%', borderRadius: 1, display: 'block' }}
              />
              {caption(label, e)}
            </Stack>
          ))}
        </Stack>
      ) : (
        <Stack spacing={0.5}>
          <Box sx={{ position: 'relative', lineHeight: 0, borderRadius: 1, overflow: 'hidden' }}>
            <Box
              component="img"
              src={before.downloadUrl}
              alt={`${t('gov.evidenceMarkup.before')}: ${before.originalFilename}`}
              sx={{ width: '100%', display: 'block' }}
            />
            <Box
              component="img"
              src={after.downloadUrl}
              alt={`${t('gov.evidenceMarkup.after')}: ${after.originalFilename}`}
              sx={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                clipPath: `inset(0 0 0 ${split}%)`,
              }}
            />
          </Box>
          <Slider
            value={split}
            onChange={(_, v) => setSplit(v as number)}
            min={0}
            max={100}
            aria-label={t('gov.evidenceMarkup.sliderLabel')}
          />
          <Stack direction="row" sx={{ justifyContent: 'space-between', gap: 1 }}>
            {caption(t('gov.evidenceMarkup.before'), before)}
            {caption(t('gov.evidenceMarkup.after'), after)}
          </Stack>
        </Stack>
      )}
    </Stack>
  );
}
